// Section 4b: Joint Venture Details (for Greenfield)
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Shield, Home, Store, Briefcase, Calculator, FileCheck } from "lucide-react";
import { IndianNumberInput } from "@/components/IndianNumberInput";

// Format currency in Crores
const formatCrores = (value) => {
  if (!value || isNaN(value)) return "₹0.00 Cr";
  return `₹${(value / 10000000).toFixed(2)} Cr`;
};

// Format number with Indian locale
const formatNumber = (value) => {
  if (!value || isNaN(value)) return "0";
  return Math.round(value).toLocaleString('en-IN');
};

export const JVRatioSection = ({
  formData,
  handleChange,
  handleSelectChange,
  fsiBreakdown
}) => {
  // Only render for greenfield projects
  if (formData.projectMode !== "greenfield") {
    return null;
  }

  // Show only the property-type ratios that actually apply to this project.
  // Rules: residential → flats only; commercial → offices/shops per saleable split (shop=0 → office 100);
  // mixed → flats + offices/shops per split.
  const pt = formData.projectType;
  const officesSplit = parseFloat(formData.saleableAreaSplitOffices) || 0;
  const shopsSplit = parseFloat(formData.saleableAreaSplitShops) || 0;
  const showFlatsRatio = pt !== "commercial"; // residential or mixed
  const showOfficesRatio = pt !== "residential" && (officesSplit > 0 || (pt === "commercial" && shopsSplit === 0));
  const showShopsRatio = pt !== "residential" && shopsSplit > 0;
  const applicableCount = (showFlatsRatio ? 1 : 0) + (showOfficesRatio ? 1 : 0) + (showShopsRatio ? 1 : 0);
  const ratioGridCols = applicableCount >= 3 ? "grid-cols-3" : (applicableCount === 2 ? "grid-cols-2" : "grid-cols-1");

  // Get JV ratios
  const jvRatioFlats = parseFloat(formData.jvRatioFlats) || parseFloat(formData.jvRatioPercent) || 30;
  const jvRatioOffices = parseFloat(formData.jvRatioOffices) || parseFloat(formData.jvRatioPercent) || 25;
  const jvRatioShops = parseFloat(formData.jvRatioShops) || parseFloat(formData.jvRatioPercent) || 20;

  // Calculate landowner's share value
  const conversionFactor = 0.092903; // sqft to sqm
  
  // Get carpet areas by type from form data (these match the revenue calculation)
  const carpetAreaFlats = parseFloat(formData.carpetAreaFlats) || 0; // sq mtr
  const carpetAreaOffices = parseFloat(formData.carpetAreaOffices) || 0;
  const carpetAreaShops = parseFloat(formData.carpetAreaShops) || 0;
  
  // Loading percentages by type
  const loadingPctFlats = parseFloat(formData.loadingPercentage) || parseFloat(formData.loadingPctFlats) || 40;
  const loadingPctOffices = parseFloat(formData.loadingPctOffices) || parseFloat(formData.loadingPercentageOffices) || 50;
  const loadingPctShops = parseFloat(formData.loadingPctShops) || parseFloat(formData.loadingPercentageShops) || 50;
  
  // Saleable area by type (sq mtr) - same as revenue calculation
  const saleableAreaFlats = carpetAreaFlats * (1 + loadingPctFlats / 100);
  const saleableAreaOffices = carpetAreaOffices * (1 + loadingPctOffices / 100);
  const saleableAreaShops = carpetAreaShops * (1 + loadingPctShops / 100);
  
  // Convert to sqft for display
  const saleAreaFlatsSqft = saleableAreaFlats / conversionFactor;
  const saleAreaOfficesSqft = saleableAreaOffices / conversionFactor;
  const saleAreaShopsSqft = saleableAreaShops / conversionFactor;

  // Sale rates
  const saleRateFlats = parseFloat(formData.saleRateFlats) || parseFloat(formData.saleRatePerSqft) || 0;
  const saleRateOffices = parseFloat(formData.saleRateOffices) || saleRateFlats;
  const saleRateShops = parseFloat(formData.saleRateShops) || saleRateFlats;

  // RR rates (for revenue sharing)
  const rrRateFlat = parseFloat(formData.rrRateFlat) || 0;
  const rrRateOffice = parseFloat(formData.rrRateOffice) || rrRateFlat;
  const rrRateShop = parseFloat(formData.rrRateShop) || rrRateFlat;
  
  // Convert RR from per sqm to per sqft
  const rrPerSqftFlat = rrRateFlat * conversionFactor;
  const rrPerSqftOffice = rrRateOffice * conversionFactor;
  const rrPerSqftShop = rrRateShop * conversionFactor;

  const isRevenueSharing = formData.jvType === "revenue_sharing";

  // Calculate landowner share by type
  let landownerFlatsArea = 0, landownerFlatsValue = 0;
  let landownerOfficesArea = 0, landownerOfficesValue = 0;
  let landownerShopsArea = 0, landownerShopsValue = 0;

  if (isRevenueSharing) {
    // Revenue sharing: landowner gets % of RR-based revenue
    landownerFlatsValue = saleAreaFlatsSqft * rrPerSqftFlat * (jvRatioFlats / 100);
    landownerOfficesValue = saleAreaOfficesSqft * rrPerSqftOffice * (jvRatioOffices / 100);
    landownerShopsValue = saleAreaShopsSqft * rrPerSqftShop * (jvRatioShops / 100);
  } else {
    // Area sharing: landowner gets % of saleable area
    landownerFlatsArea = saleAreaFlatsSqft * (jvRatioFlats / 100);
    landownerOfficesArea = saleAreaOfficesSqft * (jvRatioOffices / 100);
    landownerShopsArea = saleAreaShopsSqft * (jvRatioShops / 100);
    
    // Value at market rate
    landownerFlatsValue = landownerFlatsArea * saleRateFlats;
    landownerOfficesValue = landownerOfficesArea * saleRateOffices;
    landownerShopsValue = landownerShopsArea * saleRateShops;
  }

  const totalLandownerArea = landownerFlatsArea + landownerOfficesArea + landownerShopsArea;
  const totalLandownerValue = landownerFlatsValue + landownerOfficesValue + landownerShopsValue;

  // Check if we have data to show
  const hasCarpetAreas = carpetAreaFlats > 0 || carpetAreaOffices > 0 || carpetAreaShops > 0;
  const hasSaleRates = saleRateFlats > 0 || (isRevenueSharing && rrRateFlat > 0);

  return (
    <Card className="rounded-sm shadow-sm border-l-4 border-l-pink-400 bg-blue-50/30" data-testid="quick-jv-section">
      <CardHeader className="pb-3 bg-pink-100 rounded-t-xl border-b border-pink-200">
        <CardTitle className="font-heading tracking-tight text-lg flex items-center gap-2 text-pink-900">
          <Building2 className="h-5 w-5 text-blue-600" />
          4. Joint Venture Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Commercial GST Offset — also editable here for greenfield commercial projects */}
        {(showOfficesRatio || showShopsRatio) && (() => {
          const raw = parseFloat(formData.commercialGSTOffset);
          const offset = isNaN(raw) ? 5 : raw;
          const netRate = Math.max(0, 12 - offset).toFixed(1);
          return (
            <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-md flex flex-wrap items-end gap-3">
              <div className="space-y-1 min-w-[180px]">
                <Label htmlFor="q-jv-commercial-gst-offset" className="text-xs font-medium text-indigo-900">
                  Commercial GST Offset (%)
                </Label>
                <Input
                  id="q-jv-commercial-gst-offset"
                  name="commercialGSTOffset"
                  type="number"
                  step="0.5"
                  min="0"
                  max="12"
                  value={formData.commercialGSTOffset ?? "5"}
                  onChange={handleChange}
                  placeholder="5"
                  className="h-8 rounded-sm text-sm"
                  data-testid="quick-jv-commercial-gst-offset"
                />
              </div>
              <p className="text-xs text-indigo-800 leading-relaxed">
                Applies to <strong>Offices &amp; Shops only</strong>. GST = 12% − {offset}% offset = <strong>{netRate}%</strong> net.
              </p>
            </div>
          );
        })()}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>JV Type</Label>
            <Select
              value={formData.jvType || "area_sharing"}
              onValueChange={(value) => handleSelectChange("jvType", value)}
            >
              <SelectTrigger className="rounded-sm" data-testid="jv-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="area_sharing">Area Sharing</SelectItem>
                <SelectItem value="revenue_sharing">Revenue Sharing</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {formData.jvType === "revenue_sharing" 
                ? "Partner gets % of RR-based revenue" 
                : "Partner gets % of saleable area"}
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="q-projectDuration">Project Duration (months) *</Label>
            <Input
              id="q-projectDuration"
              name="projectDuration"
              type="number"
              value={formData.projectDuration}
              onChange={handleChange}
              placeholder="36"
              className="rounded-sm"
              data-testid="quick-duration-jv"
            />
          </div>
        </div>
        
        {/* Separate sharing for each property type - Always visible */}
        <div className="p-3 bg-white rounded border border-blue-200 space-y-3">
          <p className="text-xs font-medium text-blue-700">
            Landowner Share by Property Type ({formData.jvType === "revenue_sharing" ? "Revenue" : "Area"} Sharing)
          </p>
          <div className={`grid ${ratioGridCols} gap-3`}>
            {showFlatsRatio && (
            <div className="space-y-2">
              <Label htmlFor="q-jvRatioFlats" className="flex items-center gap-2 text-sm">
                <Home className="h-3.5 w-3.5 text-green-600" />
                Flats (%)
              </Label>
              <Input
                id="q-jvRatioFlats"
                name="jvRatioFlats"
                type="number"
                value={formData.jvRatioFlats ?? formData.jvRatioPercent ?? "30"}
                onChange={handleChange}
                placeholder="30"
                className="rounded-sm"
                data-testid="quick-jv-ratio-flats"
              />
              <p className="text-xs text-muted-foreground">
                Dev: {100 - jvRatioFlats}%
              </p>
            </div>
            )}
            {showOfficesRatio && (
            <div className="space-y-2">
              <Label htmlFor="q-jvRatioOffices" className="flex items-center gap-2 text-sm">
                <Briefcase className="h-3.5 w-3.5 text-blue-600" />
                Offices (%)
              </Label>
              <Input
                id="q-jvRatioOffices"
                name="jvRatioOffices"
                type="number"
                value={formData.jvRatioOffices ?? formData.jvRatioPercent ?? "25"}
                onChange={handleChange}
                placeholder="25"
                className="rounded-sm"
                data-testid="quick-jv-ratio-offices"
              />
              <p className="text-xs text-muted-foreground">
                Dev: {100 - jvRatioOffices}%
              </p>
            </div>
            )}
            {showShopsRatio && (
            <div className="space-y-2">
              <Label htmlFor="q-jvRatioShops" className="flex items-center gap-2 text-sm">
                <Store className="h-3.5 w-3.5 text-amber-600" />
                Shops (%)
              </Label>
              <Input
                id="q-jvRatioShops"
                name="jvRatioShops"
                type="number"
                value={formData.jvRatioShops ?? formData.jvRatioPercent ?? "20"}
                onChange={handleChange}
                placeholder="20"
                className="rounded-sm"
                data-testid="quick-jv-ratio-shops"
              />
              <p className="text-xs text-muted-foreground">
                Dev: {100 - jvRatioShops}%
              </p>
            </div>
            )}
          </div>
        </div>

        {/* Landowner Share Value Calculation */}
        <div className="p-3 bg-gradient-to-r from-purple-50 to-blue-50 rounded border border-purple-200 space-y-3">
          <div className="flex items-center gap-2">
            <Calculator className="h-4 w-4 text-purple-600" />
            <Label className="font-medium text-purple-700">Probable Landowner Share Value</Label>
          </div>
          
          {hasCarpetAreas && hasSaleRates ? (
            <div className="space-y-2">
              <div className="grid grid-cols-4 gap-2 text-xs font-medium text-muted-foreground border-b pb-2">
                <span>Type</span>
                <span className="text-right">{isRevenueSharing ? "Revenue Share" : "Area (sqft)"}</span>
                <span className="text-right">Rate</span>
                <span className="text-right">Value</span>
              </div>
              
              {saleAreaFlatsSqft > 0 && (
                <div className="grid grid-cols-4 gap-2 text-sm">
                  <span className="flex items-center gap-1"><Home className="h-3 w-3 text-green-600" /> Flats</span>
                  <span className="text-right font-mono">{isRevenueSharing ? `${jvRatioFlats}%` : formatNumber(landownerFlatsArea)}</span>
                  <span className="text-right font-mono">₹{formatNumber(isRevenueSharing ? rrPerSqftFlat : saleRateFlats)}</span>
                  <span className="text-right font-mono font-semibold text-green-700">{formatCrores(landownerFlatsValue)}</span>
                </div>
              )}
              
              {saleAreaOfficesSqft > 0 && (
                <div className="grid grid-cols-4 gap-2 text-sm">
                  <span className="flex items-center gap-1"><Briefcase className="h-3 w-3 text-blue-600" /> Offices</span>
                  <span className="text-right font-mono">{isRevenueSharing ? `${jvRatioOffices}%` : formatNumber(landownerOfficesArea)}</span>
                  <span className="text-right font-mono">₹{formatNumber(isRevenueSharing ? rrPerSqftOffice : saleRateOffices)}</span>
                  <span className="text-right font-mono font-semibold text-blue-700">{formatCrores(landownerOfficesValue)}</span>
                </div>
              )}
              
              {saleAreaShopsSqft > 0 && (
                <div className="grid grid-cols-4 gap-2 text-sm">
                  <span className="flex items-center gap-1"><Store className="h-3 w-3 text-amber-600" /> Shops</span>
                  <span className="text-right font-mono">{isRevenueSharing ? `${jvRatioShops}%` : formatNumber(landownerShopsArea)}</span>
                  <span className="text-right font-mono">₹{formatNumber(isRevenueSharing ? rrPerSqftShop : saleRateShops)}</span>
                  <span className="text-right font-mono font-semibold text-amber-700">{formatCrores(landownerShopsValue)}</span>
                </div>
              )}
              
              <div className="border-t pt-2 mt-2">
                <div className="grid grid-cols-4 gap-2 text-sm font-semibold">
                  <span className="text-purple-700">TOTAL</span>
                  <span className="text-right font-mono">{isRevenueSharing ? "-" : formatNumber(totalLandownerArea)}</span>
                  <span className="text-right">-</span>
                  <span className="text-right font-mono text-purple-700 text-base">{formatCrores(totalLandownerValue)}</span>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">
              {!hasCarpetAreas ? "Enter carpet areas by property type to see landowner value" : "Enter sale rates / RR rates to see landowner value"}
            </p>
          )}
        </div>

        {/* Security Deposit Section */}
        <div className="p-3 bg-white rounded border border-blue-200 space-y-3">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-blue-600" />
            <Label className="font-medium">Security Deposits to Landowner</Label>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2 p-3 bg-green-50 rounded border border-green-200">
              <Label htmlFor="q-securityDepositRefundable" className="flex items-center gap-2 text-green-700">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                Refundable Deposit (₹)
              </Label>
              <Input
                id="q-securityDepositRefundable"
                name="jvSecurityDepositRefundable"
                type="number"
                value={formData.jvSecurityDepositRefundable || ""}
                onChange={handleChange}
                placeholder="0"
                className="rounded-sm"
                data-testid="quick-security-deposit-refundable"
              />
              <p className="text-xs text-green-600">
                Returned at project end. In initial investment only.
              </p>
            </div>
            <div className="space-y-2 p-3 bg-red-50 rounded border border-red-200">
              <Label htmlFor="q-securityDepositNonRefundable" className="flex items-center gap-2 text-red-700">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                Non-Refundable Deposit (₹)
              </Label>
              <Input
                id="q-securityDepositNonRefundable"
                name="jvSecurityDepositNonRefundable"
                type="number"
                value={formData.jvSecurityDepositNonRefundable || ""}
                onChange={handleChange}
                placeholder="0"
                className="rounded-sm"
                data-testid="quick-security-deposit-non-refundable"
              />
              <p className="text-xs text-red-600">
                Not returned. In initial investment + P&L expenses.
              </p>
            </div>
          </div>
        </div>
        
        {/* Info box */}
        <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded border border-amber-200">
          {formData.jvType === "revenue_sharing" 
            ? `Revenue Sharing: Landowner receives Flats: ${jvRatioFlats}%, Offices: ${jvRatioOffices}%, Shops: ${jvRatioShops}% of sale revenue. Stamp duty applies on partner's share.`
            : `Area Sharing: Landowner receives Flats: ${jvRatioFlats}%, Offices: ${jvRatioOffices}%, Shops: ${jvRatioShops}% of saleable area. GST (5%) calculated on partner's area value.`
          }
        </p>

        {/* Bank Guarantee Section */}
        <div className="p-3 bg-white rounded border border-slate-200 space-y-3">
          <div className="flex items-center gap-2">
            <FileCheck className="h-4 w-4 text-slate-600" />
            <Label className="font-medium">Bank Guarantee</Label>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="q-bgRequired">BG Required?</Label>
              <Select
                value={formData.bankGuaranteeRequired || "no"}
                onValueChange={(val) => handleSelectChange("bankGuaranteeRequired", val)}
              >
                <SelectTrigger id="q-bgRequired" className="rounded-sm" data-testid="quick-bg-required">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.bankGuaranteeRequired === "yes" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="q-bgAmount">BG Amount (₹)</Label>
                  <IndianNumberInput
                    id="q-bgAmount"
                    name="bankGuaranteeAmount"
                    value={formData.bankGuaranteeAmount || ""}
                    onChange={handleChange}
                    placeholder="0"
                    className="rounded-sm"
                    data-testid="quick-bg-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="q-bgRate">BG Rate (% p.a.)</Label>
                  <Input
                    id="q-bgRate"
                    name="bgRatePA"
                    type="number"
                    value={formData.bgRatePA || "8"}
                    onChange={handleChange}
                    placeholder="8"
                    className="rounded-sm"
                    data-testid="quick-bg-rate"
                  />
                </div>
              </>
            )}
          </div>
          {formData.bankGuaranteeRequired === "yes" && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="q-bgStaged"
                  checked={formData.bgStagedRelease === "yes"}
                  onChange={(e) => handleSelectChange("bgStagedRelease", e.target.checked ? "yes" : "no")}
                  className="h-4 w-4 rounded border-slate-300"
                  data-testid="quick-bg-staged-release"
                />
                <Label htmlFor="q-bgStaged" className="cursor-pointer text-sm">
                  BG released in stages (reduces BG cost)
                </Label>
              </div>
              {formData.bgStagedRelease === "yes" && (() => {
                const bgAmount = parseFloat(formData.bankGuaranteeAmount) || 0;
                const bgRate = parseFloat(formData.bgRatePA) || 8;
                const durationMonths = parseInt(formData.projectDuration) || 24;
                const totalMonths = durationMonths + 3;
                const numStages = Math.ceil(totalMonths / 12);
                const stageMonths = Math.round(totalMonths / numStages);
                const perStage = bgAmount / numStages;
                
                let stagedCost = 0;
                const stages = [];
                for (let i = 0; i < numStages; i++) {
                  const outstanding = bgAmount - (perStage * i);
                  const months = stageMonths;
                  const cost = outstanding * (bgRate / 100) * (months / 12);
                  stagedCost += cost;
                  stages.push({ stage: i + 1, outstanding, months: stageMonths * (i + 1), released: perStage, cost });
                }
                const flatCost = bgAmount * (bgRate / 100) * (totalMonths / 12);
                const savings = flatCost - stagedCost;
                
                return (
                  <div className="text-xs bg-slate-50 p-3 rounded border border-slate-200 space-y-2">
                    <p className="font-medium text-slate-700">
                      Duration: {durationMonths} + 3 = {totalMonths} months | {numStages} stages of {stageMonths} months | ₹{(perStage / 10000000).toFixed(2)} Cr per stage
                    </p>
                    <div className="space-y-1">
                      {stages.map((s) => (
                        <div key={s.stage} className="flex justify-between text-muted-foreground">
                          <span>Stage {s.stage}: ₹{(s.outstanding / 10000000).toFixed(2)} Cr outstanding × {bgRate}% × {stageMonths}/12</span>
                          <span className="font-mono">₹{(s.cost / 100000).toFixed(1)} L</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between font-medium pt-1 border-t border-slate-200">
                      <span>Staged BG Cost</span>
                      <span className="font-mono text-chart-1">₹{(stagedCost / 10000000).toFixed(2)} Cr</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>vs Flat BG Cost (no staging)</span>
                      <span className="font-mono">₹{(flatCost / 10000000).toFixed(2)} Cr</span>
                    </div>
                    {savings > 0 && (
                      <p className="text-green-600 font-medium">Saves ₹{(savings / 10000000).toFixed(2)} Cr with staged release</p>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
